import Table from "cli-table3";
import pc from "picocolors";
import {
  applyTopToScanSummary,
  buildScanOptions,
  runFullScan,
} from "../analyzer/run-scan.js";
import type { ScanSummary, SourceKind } from "../types.js";
import { serializedGraphToDot } from "../format/to-dot.js";
import { toScanReport, type ReportVersion } from "../format/report-json.js";
import { scanSummaryToSarif, setSarifToolVersion } from "../format/to-sarif.js";
import { applyScanFailureResult, evaluateScanFailure } from "./scan-failure.js";

export type ScanCliFormat = "table" | "json" | "dot" | "sarif";

export interface ScanCliOptions {
  format?: ScanCliFormat;
  /** @deprecated use format === "json" */
  json?: boolean;
  dumpGraph?: boolean;
  top?: number;
  sourceKinds?: SourceKind[];
  ignoreGlobs?: string[];
  failAbove?: number;
  failCoveragePct?: number;
  failTop3GreedyPct?: number;
  reportVersion?: ReportVersion;
  toolVersion?: string;
  maxFiles?: number;
}

export async function runScanCommand(
  targetPath: string | undefined,
  options: ScanCliOptions,
): Promise<void> {
  const basePath = targetPath ?? ".";
  const format: ScanCliFormat =
    options.format ?? (options.json === true ? "json" : "table");

  const scanOpts = buildScanOptions(
    basePath,
    options.sourceKinds,
    options.ignoreGlobs,
    undefined,
    options.maxFiles,
  );

  if (options.dumpGraph === true) {
    const { summary, serializedGraph } = runFullScan(scanOpts);
    console.log(JSON.stringify(serializedGraph, null, 2));
    applyScanFailureResult(evaluateScanFailure(summary, options));
    return;
  }

  const { summary, serializedGraph } = runFullScan(scanOpts);
  const displaySummary = applyTopToScanSummary(summary, options.top);

  const reportVersion = options.reportVersion ?? 2;
  if (options.toolVersion) setSarifToolVersion(options.toolVersion);

  if (format === "json") {
    const report = toScanReport(displaySummary, reportVersion);
    console.log(JSON.stringify(report, null, 2));
    applyScanFailureResult(evaluateScanFailure(summary, options));
    return;
  }

  if (format === "sarif") {
    console.log(JSON.stringify(scanSummaryToSarif(summary), null, 2));
    applyScanFailureResult(evaluateScanFailure(summary, options));
    return;
  }

  if (format === "dot") {
    console.log(serializedGraphToDot(serializedGraph));
    applyScanFailureResult(evaluateScanFailure(summary, options));
    return;
  }

  printScanTables(displaySummary);
  printScanHealthFooter(displaySummary);
  applyScanFailureResult(evaluateScanFailure(summary, options));
}

function printScanHealthFooter(summary: ScanSummary): void {
  if (!summary.health) return;
  console.log("");
  console.log(pc.bold("Scan health"));
  console.log(pc.dim(summary.health.summaryLine));
}

function printScanTables(summary: ScanSummary): void {
  const n = summary.sources.length;
  const files = summary.fileCount;

  console.log(
    pc.dim(`Scanning ${files} project file${files === 1 ? "" : "s"}...`),
  );
  console.log(
    pc.bold(
      `Found ${n} any source${n === 1 ? "" : "s"}, ${summary.infectedNodeCount} infected graph node${
        summary.infectedNodeCount === 1 ? "" : "s"
      }.`,
    ),
  );

  if (n === 0) {
    printScanHealthFooter(summary);
    return;
  }

  if (summary.greedyCoverPicks.length > 0) {
    console.log("");
    console.log(pc.bold("Fix order (greedy set-cover)"));
    const greedyTable = new Table({
      head: [
        pc.dim("Pick"),
        pc.dim("Cum.%"),
        pc.dim("+Nodes"),
        pc.dim("Blast"),
        pc.dim("Bl#"),
        pc.dim("File"),
        pc.dim("Line:Col"),
        pc.dim("Kind"),
        pc.dim("Name"),
      ],
      wordWrap: true,
    });
    for (const p of summary.greedyCoverPicks) {
      greedyTable.push([
        String(p.pick),
        String(p.cumulativeCoveragePct),
        String(p.newlyCoveredNodes),
        String(p.blastRadius),
        String(p.blastRank),
        p.filePath,
        `${p.line}:${p.column}`,
        p.sourceKind,
        p.name,
      ]);
    }
    console.log(greedyTable.toString());
  }

  console.log("");
  console.log(pc.bold("By blast radius"));
  const table = new Table({
    head: [
      pc.dim("Rank"),
      pc.dim("Blast"),
      pc.dim("File"),
      pc.dim("Line:Col"),
      pc.dim("Kind"),
      pc.dim("Name"),
    ],
    wordWrap: true,
  });

  for (const s of summary.sourcesRankedByBlast) {
    table.push([
      String(s.rank),
      String(s.blastRadius),
      s.filePath,
      `${s.line}:${s.column}`,
      s.sourceKind,
      s.name,
    ]);
  }

  console.log(table.toString());
}
